import type { IconTypes } from "solid-icons";
import { TbChecklist, TbMessageQuestion, TbShieldCheck, TbSparkles, TbTags, TbUsers } from "solid-icons/tb";
import { type Component, createSignal, For, onMount, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { actionsDemo, askDemo, multiplayerDemo, offerFlowDemo, smartTagsDemo } from "~/components/marketing/demo";
import {
  ActionsMockup,
  AskMockup,
  MultiplayerMockup,
  OfferFlowMockup,
  SmartTagsMockup,
} from "~/components/marketing/mockups";
import { BRAND_TEXT, Eyebrow, MockPanel, SectionTitle, TabLink, TextLink, tabHref } from "~/components/marketing/ui";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import { FollowThroughPoints } from "./FollowThroughPoints";
import "../home.css";

type Capability = {
  id: string;
  icon: IconTypes;
  color: string;
  label: TranslationKey;
  title: TranslationKey;
  body?: TranslationKey;
  /** A short list under the body, for a capability that bundles several. */
  points?: Component;
  note?: { icon: IconTypes; text: TranslationKey };
  link: { href: string; label: TranslationKey };
  visual: Component;
};

const OfferVisual = () => {
  const { t } = useI18n();
  return <OfferFlowMockup data={offerFlowDemo(t)} />;
};
const AskVisual = () => {
  const { t } = useI18n();
  return <AskMockup data={askDemo(t)} />;
};
const TagsVisual = () => {
  const { t } = useI18n();
  return <SmartTagsMockup data={smartTagsDemo(t)} />;
};
const ActionsVisual = () => {
  const { t } = useI18n();
  return <ActionsMockup data={actionsDemo(t)} />;
};
const MultiplayerVisual = () => {
  const { t } = useI18n();
  return <MultiplayerMockup data={multiplayerDemo(t)} />;
};

const CAPABILITIES: Capability[] = [
  {
    id: "offers",
    icon: TbSparkles,
    color: "#2f6bff",
    label: "marketing.home.offers.eyebrow",
    title: "marketing.home.offers.title",
    body: "marketing.home.offers.body",
    note: { icon: TbShieldCheck, text: "marketing.home.offers.trust" },
    link: { href: "/features#live-session", label: "marketing.home.see_how" },
    visual: OfferVisual,
  },
  {
    id: "ask",
    icon: TbMessageQuestion,
    color: "#8b5cf6",
    label: "marketing.home.ask.eyebrow",
    title: "marketing.home.ask.title",
    body: "marketing.home.ask.body",
    link: { href: "/features#memory", label: "marketing.home.see_how" },
    visual: AskVisual,
  },
  {
    id: "tags",
    icon: TbTags,
    color: "#d97706",
    label: "marketing.home.tags.eyebrow",
    title: "marketing.home.tags.title",
    body: "marketing.home.tags.body",
    link: { href: "/features#auto-apply-tags", label: "marketing.home.see_how" },
    visual: TagsVisual,
  },
  {
    id: "follow-through",
    icon: TbChecklist,
    color: "#1d9e75",
    label: "marketing.home.execs.eyebrow",
    title: "marketing.home.execs.title",
    points: FollowThroughPoints,
    link: { href: "/for-executives", label: "marketing.nav.for_executives" },
    visual: ActionsVisual,
  },
  {
    id: "together",
    icon: TbUsers,
    color: "#e5484d",
    label: "marketing.home.multi.eyebrow",
    title: "marketing.home.multi.title",
    body: "marketing.home.multi.body",
    link: { href: "/features#live-session", label: "marketing.home.see_how" },
    visual: MultiplayerVisual,
  },
];

/** The open capability's copy under its tab: body, its note, and where to read more. */
const Detail = (props: { capability: Capability; class?: string }) => {
  const { t } = useI18n();
  return (
    <div class={`flex-col gap-3 ${props.class ?? ""}`} style={{ "--c": props.capability.color }}>
      <Show when={props.capability.body}>
        {(body) => <p class="text-sm leading-relaxed text-secondary-text">{t(body())}</p>}
      </Show>
      <Show when={props.capability.points}>{(points) => <Dynamic component={points()} />}</Show>
      <Show when={props.capability.note}>
        {(note) => (
          <p class="flex items-start gap-2 rounded-lg bg-(--c)/8 px-3 py-2 text-[13px] leading-snug text-secondary-text ring-1 ring-(--c)/15 dark:bg-(--c)/10">
            <Dynamic component={note().icon} class="mk-ink mt-px size-4 shrink-0" aria-hidden="true" />
            {t(note().text)}
          </p>
        )}
      </Show>
      <TextLink href={props.capability.link.href} label={t(props.capability.link.label)} class={BRAND_TEXT} />
    </div>
  );
};

const ARROW_STEPS: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1 };

/**
 * What Chief does around a call, as one tabbed section: a row of tabs, the open
 * capability's headline and copy, and its mockup at full width under them. Only
 * the open mockup is mounted, so each plays from its first frame when its tab
 * opens. From md up the tabs advance on their own, driven by the open tab's
 * progress bar finishing; the bar pauses while the pointer or focus is on the
 * tabs and while the section is off screen, and the first pick stops it for good.
 * A link ending in a tab's id (`/#tags`) lands on that tab and counts as a pick, and a pick puts
 * the tab's id in the address bar so the URL a visitor copies opens the same tab.
 */
export const CapabilitySections = () => {
  const { t } = useI18n();
  const scene = useScene();
  const [active, setActive] = createSignal(0);
  const [auto, setAuto] = createSignal(true);
  const current = () => CAPABILITIES[active()];

  const pick = (index: number) => {
    setAuto(false);
    setActive(index);
    // Replaced, not pushed, so Back leaves the page rather than stepping through tabs.
    history.replaceState(history.state, "", tabHref(location.pathname, location.search, CAPABILITIES[index].id));
  };

  // Only the browser knows the fragment, so the server renders the first tab and this swaps it after hydration.
  onMount(() => {
    const index = CAPABILITIES.findIndex((capability) => `#${capability.id}` === location.hash);
    if (index !== -1) pick(index);
  });

  const onKeyDown = (event: KeyboardEvent & { currentTarget: HTMLDivElement }) => {
    const step = ARROW_STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = (active() + step + CAPABILITIES.length) % CAPABILITIES.length;
    pick(next);
    event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
  };

  return (
    <section aria-labelledby="home-capabilities" class="px-4 py-14 sm:px-6 sm:py-16 lg:px-8">
      <div class="mx-auto max-w-7xl">
        <Eyebrow index="03" label={t("marketing.home.capabilities.eyebrow")} id="home-capabilities" />
        <SectionTitle id="home-capabilities" text={t("marketing.home.capabilities.title")} compact class="mt-3" />
        <div ref={scene.ref} class="mt-8">
          <div
            role="tablist"
            aria-labelledby="home-capabilities"
            class="group/caps flex snap-x gap-1.5 overflow-x-auto rounded-2xl border border-primary-border bg-panel p-1.5 shadow-inner sm:inline-flex sm:max-w-full sm:flex-wrap sm:overflow-visible"
            onKeyDown={onKeyDown}
          >
            <For each={CAPABILITIES}>
              {(capability, i) => (
                <button
                  type="button"
                  role="tab"
                  // The bare id doubles as the tab's URL fragment, so a link to it scrolls here.
                  id={capability.id}
                  aria-selected={active() === i()}
                  aria-controls="home-cap-panel"
                  tabIndex={active() === i() ? 0 : -1}
                  onClick={(event) => {
                    pick(i());
                    // On phones the tabs are a scrolling row; bring the picked one fully into view.
                    event.currentTarget.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
                  }}
                  class="group/tab relative flex shrink-0 cursor-pointer snap-start scroll-mt-28 items-center gap-2.5 overflow-hidden rounded-xl border py-2 ps-2 pe-4 text-sm font-semibold transition duration-200 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none active:scale-98"
                  classList={{
                    "border-(--c)/45 bg-(--c)/10 text-primary-text dark:bg-(--c)/20": active() === i(),
                    "border-primary-border bg-tile text-secondary-text shadow-sm hover:-translate-y-0.5 hover:border-(--c)/40 hover:text-primary-text hover:shadow-md motion-reduce:hover:translate-y-0":
                      active() !== i(),
                  }}
                  style={{ "--c": capability.color }}
                >
                  {/* The tab the tour opens next, so the rotation reads before it happens. */}
                  <Show when={auto() && i() === (active() + 1) % CAPABILITIES.length}>
                    <span
                      aria-hidden="true"
                      class="absolute inset-e-1.5 top-1.5 hidden size-1.5 animate-pulse rounded-full bg-(--c) md:motion-safe:block"
                    />
                  </Show>
                  <Show when={auto() && active() === i()}>
                    <span
                      aria-hidden="true"
                      class="absolute inset-x-0 bottom-0 hidden h-1 bg-(--c)/20 md:motion-safe:block"
                    >
                      <span
                        class="block size-full origin-left scale-x-0 bg-(--c) group-focus-within/caps:[animation-play-state:paused] group-hover/caps:[animation-play-state:paused]"
                        classList={{ "motion-safe:animate-[cap-progress_7s_linear_forwards]": scene.inView() }}
                        onAnimationEnd={() => setActive((active() + 1) % CAPABILITIES.length)}
                      />
                    </span>
                  </Show>
                  <span class="mk-ink relative grid size-8 shrink-0 place-items-center rounded-lg bg-(--c)/12 ring-1 ring-(--c)/20 transition-colors group-hover/tab:bg-(--c)/22 group-hover/tab:ring-(--c)/40 dark:bg-(--c)/20 dark:group-hover/tab:bg-(--c)/30">
                    <Dynamic component={capability.icon} class="size-4.5" aria-hidden="true" />
                  </span>
                  <span class="relative whitespace-nowrap">{t(capability.label)}</span>
                </button>
              )}
            </For>
          </div>

          <div role="tabpanel" id="home-cap-panel" aria-labelledby={current().id} class="mt-8">
            <div class="grid gap-4 lg:grid-cols-2 lg:gap-16">
              <h3 class="group/heading text-2xl font-semibold tracking-tight text-balance sm:text-3xl">
                {t(current().title).split("\n")[0]}
                <TabLink id={current().id} />
                <Show when={t(current().title).split("\n")[1]}>
                  {(second) => <span class="block text-secondary-text">{second()}</span>}
                </Show>
              </h3>
              <Detail capability={current()} class="flex max-w-xl" />
            </div>
            <MockPanel class="mt-8 flex flex-col justify-center lg:min-h-184">
              <div class="mx-auto w-full max-w-5xl">
                <Dynamic component={current().visual} />
              </div>
            </MockPanel>
          </div>
        </div>
      </div>
    </section>
  );
};
